import { CommonModule } from '@angular/common';
import { Component, OnDestroy, OnInit } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MultiSelectModule } from 'primeng/multiselect';
import { PaginatorModule, PaginatorState } from 'primeng/paginator';
import { CheckboxModule } from 'primeng/checkbox';
import { RatingModule } from 'primeng/rating';
import { DropdownModule } from 'primeng/dropdown';
import { CONSTANTS } from '../../../core/constants';
import { GlobalFunctions } from '../../../core/global-functions';
import { LandingService } from '../landing.service';
import { CommonService } from '../../../core/common.service';
import { debounceTime, distinctUntilChanged, Subject, takeUntil } from 'rxjs';
import { SliderModule } from 'primeng/slider';
import { TutorCardSkeletonComponent } from './tutor-card-skeleton/tutor-card-skeleton.component';

interface Price {
  name: string;
  value: string;
}

@Component({
  selector: 'app-find-tutors',
  standalone: true,
  imports: [
    CommonModule,
    MultiSelectModule,
    FormsModule,
    RouterModule,
    PaginatorModule,
    CheckboxModule,
    RatingModule,
    DropdownModule,
    SliderModule,
    TutorCardSkeletonComponent,
  ],
  templateUrl: './find-tutors.component.html',
  styleUrl: './find-tutors.component.scss',
})
export class FindTutorsComponent implements OnInit, OnDestroy {
  isOpenFilterDialog: boolean = false;
  priceFilters: Price[] | undefined;
  tutors: any[] = [];
  isMobileView: boolean = false;
  lastAppliedFilters: string = '';
  skeletonItems: number[] = Array(6).fill(0);
  isFilterApplied: boolean = false;
  baseImageUrl = CONSTANTS.baseImageUrl;
  totalRecords: number = 0;
  rows: number = 10;
  first: number = 0;
  isLoading: boolean = true;
  isLoadingSubjects: boolean = false;

  priceRange: number[] = [1, 100];
  minLimit: number = 1;
  maxLimit: number = 100;

  selectedPriceFilter: Price | undefined;
  daysArr = [
    { key: 'Mon', value: 'Monday' },
    { key: 'Tue', value: 'Tuesday' },
    { key: 'Wed', value: 'Wednesday' },
    { key: 'Thu', value: 'Thursday' },
    { key: 'Fri', value: 'Friday' },
    { key: 'Sat', value: 'Saturday' },
    { key: 'Sun', value: 'Sunday' },
  ];
  subjects: any[] | undefined;
  filterObj: any = {};
  ratings = [5, 4, 3, 2, 1];
  sortOptions: any[] = [];

  private searchSubject = new Subject<string>();
  private destroy$ = new Subject<void>();

  constructor(
    private _globalFunctions: GlobalFunctions,
    private _landingService: LandingService,
    private _commonService: CommonService
  ) {}

  ngOnInit() {
    this.checkViewport();
    if (typeof window !== 'undefined') {
      window.addEventListener('resize', this.onResize);
    }
    this.filterObj.search = '';
    this.getSubjects();
    this.priceFilters = [
      { name: 'Price high to low', value: 'phtl' },
      { name: 'Price low to high', value: 'plth' },
    ];
    this.sortOptions = [
      { name: 'Best Rating', value: 'best_rating' },
      { name: 'Price: High to Low', value: 'price_high_to_low' },
      { name: 'Price: Low to High', value: 'price_low_to_high' },
    ];

    // Setup debounced search
    this.searchSubject.pipe(
      debounceTime(500),
      distinctUntilChanged(),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.getTutorList();
    });

    this.getTutorList({}, true);
  }

  onSearchInput() {
    this.searchSubject.next(this.filterObj.search);
  }

  getSubjects() {
    this.isLoadingSubjects = true;
    this._commonService.getSubjects().subscribe(
      (result: any) => {
        if (result?.success) {
          this.subjects = result?.data?.subjects || [];
        }
        this.isLoadingSubjects = false;
      },
      () => {
        this.isLoadingSubjects = false;
      },
    );
  }

  prepareTutorObject(payload: any, isInit: boolean = false) {
    payload.page = payload.page || 1;
    payload.limit = payload.limit || 10;
    delete this.filterObj.subject_ids;
    if (
      this.filterObj.selectedSubjects &&
      this.filterObj.selectedSubjects.length
    ) {
      const subjectIds = this.filterObj.selectedSubjects?.map((subjects: any) => subjects.id || subjects._id) || [];
      payload.subject_ids = subjectIds.join(',');
    }
    if (this.filterObj.days && this.filterObj.days.length) {
      payload.days = this.filterObj.days.join(',');
    }
    if (this.filterObj.ratings && this.filterObj.ratings.length) {
      payload.ratings = this.filterObj.ratings.join(',');
    }
    if (this.filterObj.sort_by) {
      payload.sort_by = this.filterObj.sort_by;
    }
    if (!isInit) {
      payload.minRate = this.filterObj.minRate || 0;
      payload.maxRate = this.filterObj.maxRate || 0;
    }
    if (this.filterObj.search && this.filterObj.search.trim() !== '') {
      payload.search = this.filterObj.search;
    }
    return payload;
  }

  getTutorList(payload: any = {}, isInit: boolean = false) {
    this.isLoading = true;
    const prepareTutorObject = this.prepareTutorObject(payload, isInit);
    this._landingService.getTutors(prepareTutorObject).subscribe(
      (result: any) => {
        if (result?.success) {
          this.tutors = result?.data?.tutors || [];
          this.totalRecords = result?.data?.pagination?.total || 0;
          this.rows = result?.data?.pagination?.limit || 10;
          this.first =
            (result?.data?.pagination?.page - 1) *
              result?.data?.pagination?.limit || 0;

          if (isInit) {
            this.minLimit = result?.data?.pagination?.minRate || 1;
            this.maxLimit = result?.data?.pagination?.maxRate || 100;
            this.priceRange = [this.minLimit, this.maxLimit];
            this.filterObj.minRate = this.minLimit;
            this.filterObj.maxRate = this.maxLimit;
          }
          this.lastAppliedFilters = this.serializeFilters();
          this.updateFilterAppliedState();
        } else {
          this._globalFunctions.successErrorHandling(result, this, true);
        }
        this.isLoading = false;
      },
      (error: any) => {
        this._globalFunctions.errorHanding(error, this, true);
        this.isLoading = false;
      },
    );
  }

  isDayAvailable(availableDays: any[], dayValue: string): boolean {
    return availableDays?.some((d: any) => d.day_of_week === dayValue);
  }

  onSortChange(value: string) {
    this.filterObj.sort_by = value;
    this.getTutorList();
  }

  onSeachClick() {
    if (this.filterObj.search && this.filterObj.search.trim() !== '') {
      this.filterObj.search = this.filterObj.search.trim();
      this.getTutorList();
    }
  }

  clearFilters() {
    this.filterObj = {
      minRate: this.minLimit,
      maxRate: this.maxLimit,
    };
    this.priceRange = [this.minLimit, this.maxLimit];
    this.isFilterApplied = false;
    this.getTutorList({}, true);
    if (this.isMobileView) {
      this.isOpenFilterDialog = false;
    }
  }

  onPageChange(event: PaginatorState) {
    this.first = event.first || 0;
    this.rows = event.rows || 10;
    const page = (event.first || 0) / (event.rows || 10) + 1;
    this.getTutorList({ page: page, limit: this.rows });
  }

  validatePriceRange() {
    if (this.filterObj.minRate < this.minLimit) {
      this.filterObj.minRate = this.minLimit;
    }
    if (this.filterObj.maxRate > this.maxLimit) {
      this.filterObj.maxRate = this.maxLimit;
    }
    if (
      this.filterObj.maxRate &&
      this.filterObj.minRate > this.filterObj.maxRate
    ) {
      [this.filterObj.minRate, this.filterObj.maxRate] = [
        this.filterObj.maxRate,
        this.filterObj.minRate,
      ];
    }
    this.priceRange = [this.filterObj.minRate, this.filterObj.maxRate];
  }

  private onResize = () => {
    this.checkViewport();
  };

  private checkViewport() {
    if (typeof window !== 'undefined') {
      this.isMobileView = window.innerWidth < 1200;
      
      let count = 3;
      if (window.innerWidth >= 1200) {
        count = 6;
      } else if (window.innerWidth >= 768) {
        count = 4;
      }
      this.skeletonItems = Array(count).fill(0);
    }
  }

  serializeFilters(): string {
    return JSON.stringify({
      subject_ids: this.filterObj.selectedSubjects?.map((s: any) => s.id || s._id).sort() || [],
      minRate: this.filterObj.minRate,
      maxRate: this.filterObj.maxRate,
      days: this.filterObj.days ? [...this.filterObj.days].sort() : [],
      ratings: this.filterObj.ratings ? [...this.filterObj.ratings].sort() : []
    });
  }

  get hasPendingChanges(): boolean {
    return this.serializeFilters() !== this.lastAppliedFilters;
  }

  onFilterChange() {
    this.updateFilterAppliedState();
    if (!this.isMobileView) {
      this.getTutorList();
    }
  }

  updateFilterAppliedState() {
    const hasSubjects = this.filterObj.selectedSubjects && this.filterObj.selectedSubjects.length > 0;
    const hasCustomRate = 
      (this.filterObj.minRate && this.filterObj.minRate !== this.minLimit) ||
      (this.filterObj.maxRate && this.filterObj.maxRate !== this.maxLimit);
    const hasDays = this.filterObj.days && this.filterObj.days.length > 0;
    const hasRatings = this.filterObj.ratings && this.filterObj.ratings.length > 0;

    this.isFilterApplied = !!(hasSubjects || hasCustomRate || hasDays || hasRatings);
  }

  applyFilters() {
    this.getTutorList();
    this.isOpenFilterDialog = false;
  }

  toggleAccordion(event: Event) {
    const element = event.currentTarget as HTMLElement;
    const parent = element.closest('.flex.flex-col');
    if (parent) {
      const content = parent.children[1] as HTMLElement;
      if (content) {
        content.classList.toggle('hidden');
      }
      const icon = element.querySelector('.vertical');
      if (icon) {
        icon.classList.toggle('rotate-90');
      }
    }
  }

  ngOnDestroy() {
    if (typeof window !== 'undefined') {
      window.removeEventListener('resize', this.onResize);
    }
    this.destroy$.next();
    this.destroy$.complete();
  }
}
